// NPM
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useRecoilState } from 'recoil';
import { isEmpty, isNil, map, forEach, find, sortBy, isArray } from 'lodash';
import { Helmet } from "react-helmet";
import Slider from "react-slick";

// HELPERS
import { encode } from 'Helpers/enc';

// ANT COMPONENTS
import { Space, Grid, Row, Col } from 'antd';

// SHARED COMPONENTS
import { Banner, NewLayout } from 'Components/shared';


// RECOIL STATE
import { bannerListAtom, bannerActionStatusAtom } from 'Recoil/banner.atom';
import { inventoryAtom, categoryAtom, bestDealsAtom, hotSellingItemsAtom } from 'Recoil/inventory.atom';

// COMPONENTS
import ProductListingNew from 'Components/product/product-listing-new';
import ProductCategory from 'Components/product/product-category';
import VoucherListPlatformComponent from 'Components/voucher-list/platform';
import VoucherListSellerComponent from 'Components/voucher-list/seller';

// STYLE
import './homepage.scss';
import { useMediaQuery } from 'react-responsive'

// SERVICE
import { fetchBanners } from 'Services/banner.services';
import { fetchCategoryList, fetchProductSectionList } from 'Services/inventory.services';
import { getVoucherList } from 'Services/voucher.service';
import { ConsoleSqlOutlined } from '@ant-design/icons';


interface HomepageProps {
}

const Homepage: React.FC<HomepageProps> = () => {
  // VARIABLE DECLARATION
  const history = useHistory();
  const { t } = useTranslation();
  const breakpoint = Grid.useBreakpoint();

  // ATOM USE STATE
  const [inventoryState, setInventoryState] = useRecoilState<any>(inventoryAtom);
  const [categoryState, setCategoryState] = useRecoilState<any>(categoryAtom);
  const [bannerActionStatus, setBannerActionStatus] = useRecoilState<any>(bannerActionStatusAtom);
  const [bannerList, setBannerList] = useRecoilState<any>(bannerListAtom);
  const [bestDealsState, setBestDealsState] = useRecoilState<any>(bestDealsAtom);
  const [hotSellingItemsState, setHotSellingItemsState] = useRecoilState<any>(hotSellingItemsAtom);

  // USE STATE
  const [bestDealsGrid, setbestDealsGrid] = useState<any>(5);
  const [hotSellingItemsGrid, sethotSellingItemsGrid] = useState<any>(4);
  const [voucherListPlatform, setVoucherListPlatform] = useState<any>([]);
  const [voucherListSeller, setVoucherListSeller] = useState<any>([]);

  // INITIALISE VARIABLE
  const isBestDealsLoading = bestDealsState.status.fetchList === 'idle'
    && bestDealsState.status.fetchList === 'loading';

  const isHotSellingItemsLoading = hotSellingItemsState.status.fetchList === 'idle'
    && hotSellingItemsState.status.fetchList === 'loading';

  const [isLayout3, setIsLayout3]=useState(false)
  const isTabletOrMobile = useMediaQuery({ query: `(max-width: 62em)` })

  let settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          infinite: true,
          dots: true
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          initialSlide: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2
        }
      }
    ]
  };

  // INIT
  useEffect(() => {
    getBanners();
    fetchCategories();
    handleFetchProductSectionList('BEST_DEALS');
    handleFetchProductSectionList('HOT_SELLING_ITEMS');
    voucherListFunc();
  }, []);

  const getBanners = async () => {
    setBannerActionStatus({ ...bannerActionStatus, fetchList: 'loading' });

    const responseData: any = await fetchBanners({
      filters: { isLandingPage: true, active: true },
      sorting: { sortBy: 'position', mode: 'ASC' }
    });

    const layoutStyle = responseData!.data?.banners[0].layoutStyle;

    if (!isNil(responseData)) {
      setBannerActionStatus({ ...bannerActionStatus, fetchList: 'success' });


      let sliders: any = [];
      let sideBanners: any = [];
      let images: any = [];
      let files: any = [];

      if (layoutStyle == 'TEMPLATE_3') {
        setIsLayout3(true)
        forEach(responseData!.data?.banners, banner => {
          images = banner.images;
          files = banner.files_data;

          if (banner.bannerType == 'SLIDER') {
            sliders = map((sortBy(images, 'position')), (image, i) => {
              return {
                key: images[i].fileID,
                href: images[i].redirectURL,
                imageUrl: find(files, (file) => file._id == images[i].fileID)?.file_url
              }
            })
          }
        })
        setBannerList(sliders)
      } else {
        if (layoutStyle == 'TEMPLATE_1' || layoutStyle == 'TEMPLATE_2') {
          forEach(responseData!.data?.banners, banner => {
            images = banner.images;
            files = banner.files_data;

            if (banner.bannerType == 'SLIDER') {
              sliders = map((sortBy(images, 'position')), (image, i) => {
                return {
                  key: images[i].fileID,
                  href: images[i].redirectURL,
                  imageUrl: find(files, (file) => file._id == images[i].fileID)?.file_url
                }
              })
            } else if (banner.bannerType == 'STATIC') {
              sideBanners = map((sortBy(images, 'position')), (image, i) => {
                return {
                  key: images[i].fileID,
                  href: images[i].redirectURL,
                  imageUrl: find(files, (file) => file._id == images[i].fileID)?.file_url
                }
              })

            }

          })
        }
        setBannerList({ sliders, sideBanners });
      }
    }
  }

      const fetchCategories = async () => {
        setCategoryState({
          ...categoryState,
          status: { ...categoryState.status, fetch: 'loading' }
        });

        const responseData = await fetchCategoryList({ level: [1], active: true });
        if (!isNil(responseData)) {
          setCategoryState({
            ...categoryState,
            status: { ...categoryState.status, fetch: 'success' },
            list: responseData
          });
        } else {
          setCategoryState({
            ...categoryState,
            status: { ...categoryState.status, fetch: 'error' }
          });
        }
      }

      const handleFetchProductSectionList = async (type: string) => {
        if (type === 'BEST_DEALS') {
          setBestDealsState({
            ...bestDealsState,
            status: { ...bestDealsState.status, fetchList: 'loading' }
          });

          const responseData = await fetchProductSectionList({
            filters: { type, isLandingPage: true }
          });

          if (!isNil(responseData)) {
            setBestDealsState({
              ...bestDealsState,
              status: { ...bestDealsState.status, fetchList: 'success' },
              list: responseData
            });
            if (responseData.length <= 2) {
              setbestDealsGrid(2)
            }
          } else {
            setBestDealsState({
              ...bestDealsState,
              status: { ...bestDealsState.status, fetchList: 'error' }
            });
          }
        } else if (type === 'HOT_SELLING_ITEMS') {
          setHotSellingItemsState({
            ...hotSellingItemsState,
            status: { ...hotSellingItemsState.status, fetchList: 'loading' }
          });

          const responseData = await fetchProductSectionList({
            filters: { type, isLandingPage: true }
          });

          if (!isNil(responseData)) {
            setHotSellingItemsState({
              ...hotSellingItemsState,
              status: { ...hotSellingItemsState.status, fetchList: 'success' },
              list: responseData
            });
            if (responseData.length <= 2) {
              sethotSellingItemsGrid(6)
            }
          } else {
            setHotSellingItemsState({
              ...hotSellingItemsState,
              status: { ...hotSellingItemsState.status, fetchList: 'error' }
            });
          }
        }
      }

      // VOUCHER LIST FUNCTION
      const voucherListFunc = async () => {
        // REQUIRED FILTER
        const platformFilter = {
          filters: {
            voucherBy: "PLATFORM",
            sourceSite: process.env.ACCOUNT_TYPE,
          }
        };
        const sellerFilter = {
          filters: {
            voucherBy: "SELLER",
            sourceSite: process.env.ACCOUNT_TYPE,
          }
        };

        // GET VOUCHER LIST API
        const responsePlatformData = await getVoucherList(platformFilter);
        const responseSellerData = await getVoucherList(sellerFilter);

        // OUTPUT === ARRAY => VALID RESPONSE, SET VALUES
        if (isArray(responsePlatformData)) {
          setVoucherListPlatform(responsePlatformData);
        }
        if (isArray(responseSellerData)) {
          setVoucherListSeller(responseSellerData);
        }
      }
      return (
        <React.Fragment>
          <Helmet>
            {map(bestDealsState.list, prod => {
              return <meta name="description" content={prod.product.productName}/>
            })}
            {map(hotSellingItemsState.list, prod => {
              return <meta name="description" content={prod.product.productName}/>
            })}
            {map(categoryState.list, category => {
              return <meta name="description" content={category.name}/>
            })}
            <link rel="canonical" href="/"/>
            <title>
              {t('title.appTitle')}
            </title>
          </Helmet>
          <div className="homepage">
            <div className={isLayout3 ? "top--container3" : "top--container"}>
              <Space className="homepage--header container" direction="horizontal" size="small">
                {/* <div className="icon">

              <img
                src="/assets/images/homepage/tracking.svg"
                onClick={() => history.push('/purchase')}
                alt={t('label.trackMyOrder')}
              />

              <div className="text">
                {t('label.trackMyOrder')}
              </div>
            </div>

            <div className="icon">
              <img
                src='/assets/images/homepage/customer-care.svg'
                onClick={() => history.push('/support')}
                alt={t('label.customerCare')}
              />

              <div className="text">
                {t('label.customerCare')}
              </div>
            </div>

            <div className="icon">
              <img
                src='/assets/images/homepage/corporate.svg'
                onClick={() => history.push('/corporate')}
                alt={t('label.BulkOrder')}
              />

              <div className="text">
                {t('label.BulkOrderBulkOrder')}
              </div>
            </div> */}
              </Space>

          <div className="homepage--container container">
              {isLayout3? <NewLayout banners={bannerList} /> : <Banner banners={bannerList}/> }
          </div>
        </div>
        <div className="discount-type">
          <Row className="container" gutter={10}>
              <Col className="py-30" span={6}>
                  <span onClick={() => history.push(`/deals/${1}`)}>
                      <img src="/assets/images/product-discount.svg" className="mr-5"/>
                      {t('tab.ProductDiscounts')}
                  </span>
              </Col>
              <Col className="py-30" span={6}>
                  <span onClick={() => history.push(`/deals/${2}`)}>
                      <img src="/assets/images/shipping-discount.svg" className="mr-5"/>
                      {t('tab.ShippingDiscounts')}
                  </span>
              </Col>
              <Col className="py-30" span={6}>
                  <span onClick={() => history.push(`/deals/${3}`)}>
                      <img src="/assets/images/half-price.svg" className="mr-5"/>
                      {t('tab.LessThanHalfPrice')}
                  </span>
              </Col>
              <Col className="py-30" span={6}>
                  <span onClick={() => history.push(`/deals/${4}`)}>
                      <img src="/assets/images/zero-min-spend.svg" className="mr-5"/>
                      {t('tab.NoMinSpend')}
                  </span>
              </Col>
          </Row>
            </div>

        {!isEmpty(categoryState?.list) &&
        <div className="categories">
          <div className="container">
            <h2 className="title pt-20 pb-10">
              {t('title.categories')}
            </h2>

            <ProductCategory
              categories={map(categoryState?.list, (c, index) => {
                return {
                  _id: c._id,
                  name: c.name,
                  imageUrl: c?.image_data?.file_url || "",
                  href: `?category=${encode(c._id)}`,
                }
              })}
            />
          </div>
        </div>
        }

        {!isEmpty(bestDealsState.list) &&
        <div className="best-deals">
          <div className="container">
            <Row gutter={16}>
              <Col md={4} sm={24} xs={24} className="title-container">
                <div>
                  <img src='/assets/images/homepage/best-deal.svg' width={50}/>
                </div>
                <h2 className="title">
                  {t('title.bestDeals')}
                </h2>
              </Col>
              <Col md={20} sm={24} xs={24}>
                {!isBestDealsLoading &&
                <React.Fragment>
                  <div className="product-listing-new">
                    <Slider {...settings}>
                      {map(bestDealsState.list, (o, index) => {
                        const price = parseFloat(o?.stockVariants[0]?.value);
                        const stockLimit = o.stockVariants[0]?.stockLimit ? parseInt(o.stockVariants[0]?.stockLimit) : 0;
                        const percentOff = 10;
                        const discountPrice = price + (price * 10 * 0.01);
                        const discounted = o.discountPrice
                          ? (parseFloat(o.price) - parseFloat(o.discountPrice)).toFixed(2) : 0;
                        return (
                          <div className="p-10">
                          <ProductListingNew
                            grid={bestDealsGrid}
                            product={{
                              displayType: 'saved',
                              imageUrl: o.featuredImage?.image,
                              name: o.product.productName,
                              productType: o.product.productType,
                              price: discountPrice.toFixed(2),
                              stockLimit: stockLimit,
                              discountPrice: price.toFixed(2),
                              percentOff: percentOff,
                              productData: o,
                              supplierLocation: o.orgData?.primaryAddress?.city || null,
                              disablePurchase: o.disablePurchase,
                              href: `/products/${encode(o.inventoryID)}?name=${o.product.productName}`,
                              discounted: discounted
                            }}
                          />
                          </div>
                        )
                      })}
                    </Slider>
                  </div>
                </React.Fragment>
                }
              </Col>
            </Row>
          </div>
        </div>
        }

        {!isEmpty(hotSellingItemsState.list) &&
        <div className="hot-selling">
          <div className="container">
            <h2 className="title py-10 mb-20">
              {t('title.hotSellingItems')}
            </h2>

            {!isHotSellingItemsLoading &&
            <React.Fragment>
              <div className="product-listing-new">
                <Row gutter={24}>

                {map(hotSellingItemsState.list, (o, index) => {
                  const price = parseFloat(o.stockVariants[0]?.value);
                  const stockLimit = o.stockVariants[0]?.stockLimit ? parseInt(o.stockVariants[0]?.stockLimit) : 0;
                  const percentOff = 10;
                  const discountPrice = price + (price * 10 * 0.01);
                  const discounted = o.discountPrice
                    ? (parseFloat(o.price) - parseFloat(o.discountPrice)).toFixed(2) : 0;
                  return (
                    <Col className="p-10" lg={hotSellingItemsGrid} md={hotSellingItemsGrid} sm={12} xs={12}>
                      <ProductListingNew
                        grid={hotSellingItemsGrid}
                        product={{
                          displayType: 'saved',
                          imageUrl: o.featuredImage?.image,
                          name: o.product.productName,
                          productType: o.product.productType,
                          price: discountPrice.toFixed(2),
                          stockLimit: stockLimit,
                          discountPrice: price.toFixed(2),
                          percentOff: percentOff,
                          productData: o,
                          supplierLocation: o.orgData?.primaryAddress?.city || null,
                          disablePurchase: o.disablePurchase,
                          href: `/products/${encode(o.inventoryID)}?name=${o.product.productName}`,
                          discounted: discounted
                        }}
                      />
                    </Col>
                  )
                })}
              </Row>
              </div>
            </React.Fragment>}
          </div>
        </div>}
            <div className="voucher-list">
              <div className="container">
                {
                  isArray(voucherListSeller) && !isEmpty(voucherListSeller) ?
                    <div className="pt-20">
                      <VoucherListSellerComponent hideTitle={false} voucherList={voucherListSeller}></VoucherListSellerComponent>
                      <div className="py-10"></div>
                    </div>
                    : null
                }
                {
                  isArray(voucherListPlatform) && !isEmpty(voucherListPlatform) ?
                    <VoucherListPlatformComponent bannerVoucherList={voucherListPlatform}></VoucherListPlatformComponent>
                    : null
                }
              </div>
            </div>

            <div className="bottom--container">
              <div className="bottom--container-inner container">
                <h3 className="title">{t('title.appTitle')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('txt.appDescription') }} />

                <h3 className="title">{t('title.paymentMethod')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('txt.paymentMethodDescription') }} />

                <h3 className="title">{t('title.deliveryServices')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('txt.deliveryServicesDescription') }} />
              </div>
            </div>
          </div>
        </React.Fragment>
      )
    }

  export default Homepage;
